import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ColumnDatatable,
    FilterDatatable,
    FilterOptionValue,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { useDispatch, useSelector } from 'react-redux';
import {
    CATALOGS_SLICE_NAME,
    CatalogSelector,
    useOffreService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { CatalogueJsonldCatalogueRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { Box, CircularProgress, Typography } from '@mui/material';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { AnyAction } from '@reduxjs/toolkit';
import { UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import ActionMenuGrilleTarifaire from '../components/ActionMenuGrilleTarifaire';
import useFilterOptions from '../../Offres/Catalog/useFilterOptions';
import { OffreFormalite, useOffreFormalite } from '../hook/useOffreFormalite';
import useOffrePrescripteur, { Tiers } from '../hook/useOffrePrescripteur';
import TarifCell from '../components/DataTableCell/TarifCell';
import { useOrigine } from '../../ParcoursFormalite/hooks';
import useOfferCache from '../../Offres/cache/useOfferCache';
import { PrescriberOfferChip } from '../../Offres/components/PrescriberOfferChip';

function calculByType(type: string, row: CatalogueJsonldCatalogueRead): number | string {
    switch (type) {
        case 'ht':
            return (row.tarifHT ?? 0.0).toFixed(2);
        case 'ttc':
            return row.tarifTTC ?? '0.00';
        default:
            return '0.00';
    }
}

export const GrilleTarifaire: React.FunctionComponent<{
    selectedPrescriberId?: number;
    showOrigineFilter?: boolean;
    showTTC: boolean;
    showNumberCatalogue: boolean;
    showOffer: boolean;
    showElement: boolean;
    showAction: boolean;
    showChips?: boolean;
    surchargeHonoraire: boolean;
    surchargeFD: boolean;
    surchargeAL: boolean;
    waitPrescripteur: boolean;
    showDate?: boolean;
}> = function ({
    selectedPrescriberId,
    showOrigineFilter = false,
    showTTC = true,
    showNumberCatalogue = false,
    showOffer = false,
    showElement = false,
    showAction = false,
    showChips = true,
    surchargeHonoraire = false,
    surchargeFD = false,
    surchargeAL = false,
    waitPrescripteur = false,
    showDate = false,
}) {
    const xIdSociete = useSociete();
    const datasource: DataSource<CatalogueJsonldCatalogueRead> = useSelector(CatalogSelector).main;
    const { getCatalogs } = useOffreService();
    const filterOptions = useFilterOptions();
    const { searchOfferFormalite } = useOffreFormalite();
    const { getPrescriber } = useOffrePrescripteur();
    const [editPrescripteur, setEditPrescripteur] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(waitPrescripteur);
    const origineId = useOrigine();
    const hasDispatchedOnMount = useRef(false);
    const roleChecker = UseKeycloakCheckRole();
    const isInterne = roleChecker('realm:interne');

    const [idPrescriber, setIdPrescriber] = useState<number | undefined>(undefined);

    const dispatch = useDispatch();
    const refreshData = useCallback(
        () => dispatch(getCatalogs({}) as unknown as AnyAction),
        [dispatch, getCatalogs],
    );
    useEffect(
        () => () => {
            dispatch({
                type: `${CATALOGS_SLICE_NAME}/resetmainFilter`,
            });
            hasDispatchedOnMount.current = false;
        },
        // doit se jouer au démontage du composant
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    useEffect(() => {
        if (!selectedPrescriberId) {
            return;
        }
        dispatch({
            type: `${CATALOGS_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'surchargePrescripteurPrescripteur',
                value: selectedPrescriberId,
            },
        });
        dispatch({
            type: `${CATALOGS_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'origineId',
                value: origineId,
            },
        });
        setIdPrescriber(selectedPrescriberId);
        setLoading(false);
    }, [selectedPrescriberId, origineId, dispatch]);

    useEffect(() => {
        if (datasource.filters.surchargePrescripteurPrescripteur && !selectedPrescriberId) {
            setIdPrescriber(Number(datasource.filters.surchargePrescripteurPrescripteur));
        } else if (!selectedPrescriberId) {
            setIdPrescriber(undefined);
        }
    }, [datasource.filters.surchargePrescripteurPrescripteur, selectedPrescriberId]);

    useEffect(() => {
        if (!hasDispatchedOnMount.current) {
            const today = new Date().toISOString().split('T')[0];
            dispatch({
                type: `${CATALOGS_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'dateDebutBefore',
                    value: today,
                },
            });
            dispatch({
                type: `${CATALOGS_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'dateFinAfter',
                    value: today,
                },
            });
            hasDispatchedOnMount.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // à se jouer qu'au montage du composant

    useEffect(() => {
        if (hasDispatchedOnMount.current !== undefined) {
            refreshData();
        }
    }, [hasDispatchedOnMount, refreshData]);

    const dataColumnsRender: ColumnDatatable<CatalogueJsonldCatalogueRead>[] = useMemo(() => {
        const isEditPrescripteur: boolean = !!(
            surchargeHonoraire ||
            (datasource.filters.surchargePrescripteurPrescripteur && !selectedPrescriberId)
        );
        setEditPrescripteur(isEditPrescripteur);
        const dataColumns: ColumnDatatable<CatalogueJsonldCatalogueRead>[] = [];
        if (showNumberCatalogue) {
            dataColumns.push({
                isDisplayed: true,
                sortable: true,
                label: 'N°',
                field: 'id',
                render: ({ id }) => <Typography variant="l2dbodyxs">{id}</Typography>,
                sx: {
                    fontWeight: 500,
                    paddingLeft: '40px',
                },
            });
        }
        if (showOffer) {
            dataColumns.push({
                isDisplayed: true,
                sortable: true,
                label: 'Offres',
                field: 'offre.libelle',
                render: ({ offre }) => (
                    <FaOptionIcon icon={['fad', offre?.iconeName as IconName]} />
                ),
            });
        }

        dataColumns.push(
            {
                isDisplayed: true,
                label: 'Formalités',
                sortable: true,
                field: 'groupement.libelle',
                render: ({ groupement, nomCommercial }) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Typography
                            variant="l2dbodys"
                            sx={{ color: 'text.gris70' }}
                        >
                            {groupement?.libelle}
                        </Typography>
                        <Typography variant="l2dbodys">{nomCommercial}</Typography>
                    </Box>
                ),
            },
            {
                isDisplayed: false,
                label: 'Date début',
                sortable: true,
                field: 'dateDebut',
                render: ({ dateDebut }) => <Typography variant="l2dbodyxs">{dateDebut}</Typography>,
            },
            {
                isDisplayed: false,
                label: 'Date fin',
                sortable: true,
                field: 'dateFin',
                render: ({ dateFin }) => <Typography variant="l2dbodyxs">{dateFin}</Typography>,
            },
            {
                isDisplayed: true,
                sortable: true,
                label: 'Honoraires',
                field: undefined,
                render: (row) => (
                    <Box>
                        <TarifCell
                            type="honoraire"
                            row={row}
                            editable={isEditPrescripteur}
                            idPrescriber={idPrescriber}
                            showChips={showChips}
                        />
                    </Box>
                ),
            },
            {
                isDisplayed: true,
                sortable: true,
                label: 'Frais de dossier',
                field: undefined,
                render: (row) => (
                    <TarifCell
                        type="frais"
                        row={row}
                        editable={surchargeFD}
                        showChips={showChips}
                    />
                ),
            },
            {
                isDisplayed: true,
                sortable: true,
                label: 'Annonces légales',
                field: undefined,
                render: (row) => (
                    <TarifCell
                        type="annonce"
                        row={row}
                        editable={surchargeAL}
                        showChips={showChips}
                    />
                ),
            },
        );
        if (showElement) {
            dataColumns.push({
                isDisplayed: true,
                sortable: true,
                label: 'Elements',
                field: undefined,
                render: (row) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '8px',
                            color: 'text.gris70',
                        }}
                    >
                        <Typography
                            variant="l2dbodyxs"
                            sx={{ display: 'flex', flexDirection: 'row', gap: '1px' }}
                        >
                            <FaOptionIcon icon={['fad', 'paperclip']} />
                            {(row.package?.formalite?.documents?.length as number) ??
                                0 + (row.documents?.length as number) ??
                                0}
                        </Typography>
                        <Typography
                            variant="l2dbodyxs"
                            sx={{ display: 'flex', flexDirection: 'row', gap: '1px' }}
                        >
                            <FaOptionIcon icon={['fad', 'money-check-pen']} />
                            {row.options?.length}
                        </Typography>
                    </Box>
                ),
            });
        }
        dataColumns.push({
            isDisplayed: true,
            sortable: true,
            label: 'Total HT',
            field: undefined,
            render: (row) => (
                <Box
                    display="flex"
                    flexDirection="row"
                    gap="2px"
                    sx={{ color: 'text.primary' }}
                >
                    <Typography variant="l2dbodyxs">{calculByType('ht', row)}</Typography>
                    <Typography variant="l2dbodyxs">€</Typography>
                </Box>
            ),
        });
        if (showTTC) {
            dataColumns.push({
                isDisplayed: true,
                sortable: true,
                label: 'Total TTC',
                field: undefined,
                render: (row) => (
                    <Box
                        display="flex"
                        flexDirection="row"
                        gap="2px"
                        sx={{ color: 'text.primary' }}
                    >
                        <Typography variant="l2dbodyxs">{calculByType('ttc', row)}</Typography>
                        <Typography variant="l2dbodyxs">€</Typography>
                    </Box>
                ),
            });
        }
        if (showAction) {
            dataColumns.push({
                label: 'Actions',
                displayLabel: true,
                render: (row) =>
                    row.id && (
                        <ActionMenuGrilleTarifaire
                            showPackage
                            showCatalogue
                            showPrescripteur={editPrescripteur}
                            catalogue={row}
                        />
                    ),
                isDisplayed: true,
            });
        }

        return dataColumns;
    }, [
        surchargeHonoraire,
        datasource.filters.surchargePrescripteurPrescripteur,
        selectedPrescriberId,
        showNumberCatalogue,
        showOffer,
        showElement,
        showTTC,
        showAction,
        showChips,
        idPrescriber,
        surchargeFD,
        surchargeAL,
        editPrescripteur,
    ]);

    useOfferCache();
    /** Generic function : Generate filter object */
    const getFilter = (
        label: string,
        field: string,
        options: FilterOptionValue[],
    ): FilterDatatable => ({
        label,
        field,
        type: 'staticSelect' as const,
        options,
        renderFilterList: (value: string) => {
            const selectedOption = options.find((option) => option.value === value);
            return selectedOption ? selectedOption.label : 'Aucun';
        },
    });

    const catalogsFilters: FiltersDatatableList = [
        ...(!selectedPrescriberId
            ? [
                  getFilter('Offre', 'offreId', filterOptions.offers),
                  getFilter('Domaine', 'domaineId', filterOptions.domains),
                  {
                      label: 'Prescripteur',
                      field: 'surchargePrescripteurPrescripteur',
                      type: 'selectApi',
                      searchFunction: getPrescriber,
                      renderItem: (item: Tiers) => item.raisonSociale,
                      valueMap: (item: Tiers) => item.id as unknown as string,
                  } as FilterDatatable,
              ]
            : []),
        ...(showOrigineFilter ? [getFilter('Origine', 'origineId', filterOptions.origins)] : []),
        getFilter('Regroupement', 'groupementId', filterOptions.groups),
        {
            label: 'Formalités',
            field: 'packageFormaliteId',
            type: 'selectApi',
            searchFunction: searchOfferFormalite,
            renderItem: (item: OffreFormalite) => item.libelle as unknown as string,
            valueMap: (item: OffreFormalite) => item.id as unknown as string,
        },
        ...(showDate
            ? [
                  {
                      label: 'Date début',
                      field: 'dateDebutBefore',
                      type: 'date',
                  },
                  {
                      label: 'Date de fin',
                      field: 'dateFinAfter',
                      type: 'date',
                  },
              ]
            : []),
    ];

    if (loading && waitPrescripteur) {
        return <CircularProgress sx={{ height: '500px' }} />;
    }

    return (
        <>
            {isInterne && selectedPrescriberId && (
                <PrescriberOfferChip
                    tiersId={selectedPrescriberId}
                    onOfferUpdate={refreshData}
                />
            )}
            <StoreDatatable
                dataSource={datasource}
                columns={dataColumnsRender}
                fetchData={getCatalogs}
                filters={catalogsFilters}
                localStorageKey="FlexyStoreDatatableGrilleTarifaire"
                observers={[xIdSociete]}
                sx={{ marginBottom: '2.25em' }}
            />
        </>
    );
};

export default GrilleTarifaire;
